import { useState, useEffect } from 'preact/hooks';
import { Icons } from './icons/icon-set';
import { useClickOutside } from './use-click-outside';
import type { PhpVersion } from '../data/php-versions';

interface VersionSelectorProps {
  all: PhpVersion[];
  selected: string[];
  onToggle: (v: string) => void;
  /** version keys whose runtime is already cached locally. */
  downloaded?: string[];
}

/** How many version pills the trigger shows before collapsing the rest into a
 * "+N" badge. Phones get 1 (keeps the topbar on a single compact row no matter
 * how many versions are selected); wider screens get 3. */
function useMaxPills(): number {
  const query = '(max-width: 720px)';
  // Init to the SSR/desktop value (3) so the first client (hydration) render
  // matches the prerendered HTML; the effect reads the real matchMedia after mount.
  const [max, setMax] = useState(3);
  useEffect(() => {
    if (typeof matchMedia === 'undefined') return;
    const mq = matchMedia(query);
    const sync = () => setMax(mq.matches ? 1 : 3);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return max;
}

export function VersionSelector({ all, selected, onToggle, downloaded = [] }: VersionSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  const sortedSel = all.filter((v) => selected.includes(v.v));
  const maxPills = useMaxPills();

  return (
    <div class="verselect" ref={ref}>
      <button class="verselect-trigger" onClick={() => setOpen((o) => !o)}>
        <Icons.chip size={16} style={{ opacity: 0.7 }} />
        <span class="hide-sm">PHP</span>
        <span class="ver-pills">
          {sortedSel.slice(0, maxPills).map((v) => (
            <span key={v.v} class="ver-pill">
              {v.v}
            </span>
          ))}
          {sortedSel.length > maxPills && (
            <span class="ver-count">+{sortedSel.length - maxPills}</span>
          )}
        </span>
        <Icons.chevron size={15} style={{ opacity: 0.5 }} />
      </button>
      {open && (
        <div class="popover">
          <div class="popover-head">
            <span>Run in parallel</span>
            <span style={{ textTransform: 'none', fontWeight: 700, color: 'var(--primary-strong)' }}>
              {selected.length} active
            </span>
          </div>
          <div class="ver-list">
            {all.map((v) => (
              <div
                key={v.v}
                class="ver-item"
                data-on={selected.includes(v.v)}
                onClick={() => onToggle(v.v)}
              >
                <span class="check">
                  <Icons.check size={12} />
                </span>
                <span class="ver-num">{v.v}</span>
                {v.tag === 'latest' && <span class="ver-tag latest">latest</span>}
                {v.tag === 'eol' && <span class="ver-tag eol">EOL</span>}
                {downloaded.includes(v.v) ? (
                  <span class="ver-size cached">
                    <Icons.disk size={13} /> cached
                  </span>
                ) : (
                  <span class="ver-size">
                    <Icons.download size={13} /> {v.sizeMB} MB
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
